import { motion } from "framer-motion";
import { useEnquiry } from "@/components/site/EnquiryProvider";
import { ArrowUpRight } from "lucide-react";

const projects = [
  {
    slug: "the-sacred-wild",
    category: "Solo Exhibition",
    year: "2025",
    title: "The Sacred Wild",
    body: "A body of 40+ acrylics and watercolours exploring temple architecture, birds, pomegranates and sacred flora \u2014 inspired by pilgrimages through Tamil Nadu and Chettinad.",
    image: "/artworks/sacred/sacred-guardians.jpg",
    href: "#sacred-wild",
    aspect: "aspect-[5/6]",
  },
  {
    slug: "framing-flowers",
    category: "Solo Exhibition \u00b7 Nairobi",
    year: "2024",
    title: "Framing Flowers \u2014 Spirit of Kenya",
    body: "Painterly photographs and paintings born of Kenya\u2019s flower markets, Maasai Mara wildlife and the endearing everyday of Nairobi.",
    image: "/artworks/photos/if-i-were-a-rose.jpg",
    href: "#exhibition",
    aspect: "aspect-[4/3]",
  },
  {
    slug: "intimate-affair",
    category: "Mixed Media Series",
    year: "2023 \u2014 24",
    title: "An Intimate Affair",
    body: "Twelve mixed-media studies celebrating the feminine \u2014 a layered meditation on resilience, blossom and Shakti.",
    image: "/artworks/intimate/intimate-1.jpg",
    href: "#collection",
    aspect: "aspect-[3/4]",
  },
  {
    slug: "studio-commissions",
    category: "Commissions & Installations",
    year: "Ongoing",
    title: "Studio Commissions",
    body: "Bespoke paintings, murals and sculpture installations for private collectors, hospitality projects and corporate environments across India.",
    image: "/artworks/premium/textured-reflections.jpeg",
    href: "enquire",
    aspect: "aspect-[4/5]",
  },
];

function ProjectCard({ p, i }) {
  const { openEnquiry } = useEnquiry();
  const isEnquire = p.href === "enquire";
  const commonProps = {
    "data-testid": `project-${p.slug}`,
    "data-cursor": isEnquire ? "Enquire" : "Explore",
    className:
      "group block text-left w-full",
  };

  const onClick = isEnquire
    ? () =>
        openEnquiry({
          is_service: true,
          slug: `project-${p.slug}`,
          title: p.title,
          tagline: p.category,
        })
    : undefined;

  const Wrapper = ({ children }) =>
    isEnquire ? (
      <button type="button" onClick={onClick} {...commonProps}>
        {children}
      </button>
    ) : (
      <a href={p.href} {...commonProps}>
        {children}
      </a>
    );

  const offset = i % 2 === 1 ? "md:mt-24" : "";

  return (
    <motion.div
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay: (i % 2) * 0.08, ease: [0.16, 1, 0.3, 1] }}
      className={offset}
    >
      <Wrapper>
        <div className={`relative overflow-hidden bg-sand gallery-shadow ${p.aspect}`}>
          <img
            src={p.image}
            alt={p.title}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.06]"
          />
          <div className="absolute top-4 left-4 bg-paper/95 px-3 py-1.5 font-body text-[10px] tracking-[0.2em] uppercase text-ink">
            {p.category}
          </div>
          <div className="absolute top-4 right-4 h-11 w-11 flex items-center justify-center bg-paper text-ink translate-y-3 opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
            <ArrowUpRight className="h-5 w-5" />
          </div>
        </div>
        <div className="pt-5 flex items-start justify-between gap-4">
          <div>
            <h3 className="font-serif text-2xl md:text-3xl text-ink leading-tight tracking-tight group-hover:text-ochre transition-colors">
              {p.title}
            </h3>
            <p className="mt-3 font-body text-base text-ink-soft leading-relaxed max-w-md">
              {p.body}
            </p>
          </div>
          <span className="font-serif text-2xl text-ink-soft shrink-0">{p.year}</span>
        </div>
      </Wrapper>
    </motion.div>
  );
}

export default function Projects() {
  const left = projects.filter((_, i) => i % 2 === 0);
  const right = projects.filter((_, i) => i % 2 === 1);

  return (
    <section
      id="projects"
      className="relative py-24 md:py-36 bg-paper overflow-hidden"
      data-testid="projects-section"
    >
      <div className="max-w-screen-2xl mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-8 items-end mb-16 md:mb-24">
          <div className="md:col-span-8">
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="font-body text-[11px] tracking-[0.35em] uppercase text-ochre mb-5"
            >
              Projects &amp; Portfolio
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="font-serif text-4xl sm:text-5xl lg:text-7xl leading-[1] text-ink tracking-tight"
            >
              Selected work
              <br />
              &amp; <span className="italic text-ochre">exhibitions.</span>
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="md:col-span-4 font-body text-base text-ink-soft leading-relaxed"
          >
            A selection of solo exhibitions, series and studio commissions from
            recent years. Each opens into its own body of work &mdash; or a
            conversation with the studio.
          </motion.p>
        </div>

        <div className="grid md:grid-cols-2 gap-x-8 lg:gap-x-16 gap-y-16 md:gap-y-24">
          <div className="flex flex-col gap-16 md:gap-24">
            {left.map((p, i) => (
              <ProjectCard key={p.slug} p={p} i={i * 2} />
            ))}
          </div>
          <div className="flex flex-col gap-16 md:gap-24">
            {right.map((p, i) => (
              <ProjectCard key={p.slug} p={p} i={i * 2 + 1} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
